import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import ConfigProvider from "./common/ConfigProvider";
import Tooltip from "./common/Tooltip";
import Avatar from "./common/Avatar";
import Empty from "./common/Empty";
import Card from "./common/Card";
import TrebbleSkeleton from "./common/Skeleton";
import TrebbleFilterCascader from "./common/FilterCascader";
import useBackboneModel from "./helper/useBackboneModel";
import useDefaultTrebbleAntDTokenConfig from "./helper/useDefaultTrebbleAntDTokenConfig";
import ti18n from "i18n!nls/Sequenceri18n";

const COMMENT_FILTER = {
    ALL: "all",
    OPEN: "open",
    RESOLVED: "resolved",
    FOR_ME: "forMe",
};

function t(key, fallback) {
    const value = typeof window !== "undefined" && window.getI18n ? window.getI18n(ti18n, key) : null;
    return value || fallback || key;
}

function formatCompactRelativeTime(date) {
    const createdAt = moment(date);
    if (!createdAt.isValid()) {
        return "";
    }

    const secondsAgo = Math.max(0, moment().diff(createdAt, "seconds"));
    const units = [
        { limit: 60, value: 0, unit: "", key: "COMMENT_TIME_NOW", fallback: "now" },
        { limit: 3600, value: Math.max(1, Math.floor(secondsAgo / 60)), unit: t("COMMENT_TIME_MINUTE_COMPACT", "min") },
        { limit: 86400, value: Math.floor(secondsAgo / 3600), unit: t("COMMENT_TIME_HOUR_COMPACT", "h") },
        { limit: 2592000, value: Math.floor(secondsAgo / 86400), unit: t("COMMENT_TIME_DAY_COMPACT", "d") },
        { limit: 31536000, value: Math.floor(secondsAgo / 2592000), unit: t("COMMENT_TIME_MONTH_COMPACT", "mo") },
        { limit: Infinity, value: Math.floor(secondsAgo / 31536000), unit: t("COMMENT_TIME_YEAR_COMPACT", "y") },
    ];
    const match = units.find(function(unit) {
        return secondsAgo < unit.limit;
    });
    if (match && match.key) {
        return t(match.key, match.fallback);
    }
    return t("COMMENT_TIME_AGO_FORMAT", "{value}{unit} ago")
        .replace("{value}", match.value)
        .replace("{unit}", match.unit);
}

function getCommentMessages(comment) {
    return comment && comment.messages ? comment.messages : [];
}

function getCommentLatestMessage(comment) {
    const messages = getCommentMessages(comment);
    return messages[messages.length - 1] || null;
}

function getAuthorInitials(author) {
    if (!author) {
        return "T";
    }
    return author.initials || (author.name || author.email || "T").substring(0, 2).toUpperCase();
}

function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value || "");
}

function stopEditorShortcutPropagation(event) {
    if (event && event.stopPropagation) {
        event.stopPropagation();
    }
}

function CommentText({ text }) {
    const parts = (text || "").split(/(@[a-zA-Z0-9_.+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z0-9.-]+|@[a-zA-Z0-9_.-]+)/g);
    return (
        <span>
            {parts.map(function(part, index) {
                if (part && part[0] === "@") {
                    return <span className="editor-comments-mention" key={index}>{part}</span>;
                }
                return <span key={index}>{part}</span>;
            })}
        </span>
    );
}

CommentText.propTypes = {
    text: PropTypes.string,
};

function CommentMessage({ message }) {
    const author = message.author || {};
    return (
        <div className="editor-comments-message">
            <Avatar useEditorTheme={true} size={22} src={author.profileImageUrl || undefined}>
                {!author.profileImageUrl ? getAuthorInitials(author) : null}
            </Avatar>
            <div className="editor-comments-message-body">
                <div className="editor-comments-message-meta">
                    <span className="editor-comments-author">{author.name || author.email || t("UNKNOWN", "Unknown")}</span>
                    <span>{formatCompactRelativeTime(message.createdAt)}</span>
                </div>
                <div className="editor-comments-message-text">
                    <CommentText text={message.body} />
                </div>
                {message.mentions && message.mentions.some(function(mention) { return mention.type === "emailReviewerInvite"; }) ? (
                    <div className="editor-comments-invite-note">
                        {t("COMMENT_REVIEWER_INVITE_PENDING", "Reviewer invite staged")}
                    </div>
                ) : null}
            </div>
        </div>
    );
}

CommentMessage.propTypes = {
    message: PropTypes.object.isRequired,
};

function uniqueMentionMembers(members) {
    const seen = {};
    return (members || []).filter(function(member) {
        const key = member && (member.email || member.username || member.userUID || member.label);
        if (!key || seen[key]) {
            return false;
        }
        seen[key] = true;
        return true;
    });
}

function MentionMenu({ query, members, onSelect }) {
    const normalizedQuery = (query || "").toLowerCase();
    const visibleMembers = uniqueMentionMembers(members).filter(function(member) {
        if (!normalizedQuery) {
            return true;
        }
        return (member.name && member.name.toLowerCase().indexOf(normalizedQuery) !== -1)
            || (member.username && member.username.toLowerCase().indexOf(normalizedQuery) !== -1)
            || (member.email && member.email.toLowerCase().indexOf(normalizedQuery) !== -1);
    }).slice(0, 5);

    const shouldShowEmailInvite = isValidEmail(normalizedQuery)
        && !visibleMembers.some(function(member) {
            return member.email && member.email.toLowerCase() === normalizedQuery;
        });

    if (!visibleMembers.length && !shouldShowEmailInvite) {
        return null;
    }

    return (
        <div className="editor-comments-mention-menu">
            {visibleMembers.length ? (
                <div className="editor-comments-mention-section-label">{t("PEOPLE", "People")}</div>
            ) : null}
            {visibleMembers.map(function(member) {
                const label = member.username || member.email;
                return (
                    <button
                        type="button"
                        key={member.userUID || member.email || member.username}
                        className="editor-comments-mention-option"
                        onMouseDown={function(event) {
                            event.preventDefault();
                            onSelect("@" + label);
                        }}
                    >
                        <Avatar useEditorTheme={true} size={24} src={member.profileImageUrl || undefined}>
                            {!member.profileImageUrl ? getAuthorInitials(member) : null}
                        </Avatar>
                        <span className="editor-comments-mention-option-copy">
                            <span className="editor-comments-mention-option-name">{member.name || label}</span>
                            <small>{member.email || ("@" + label)}</small>
                        </span>
                    </button>
                );
            })}
            {shouldShowEmailInvite ? (
                <>
                    <div className="editor-comments-mention-section-label">{t("COMMENT_INVITE_BY_EMAIL", "Invite by email")}</div>
                    <button
                        type="button"
                        className="editor-comments-mention-option"
                        onMouseDown={function(event) {
                            event.preventDefault();
                            onSelect("@" + normalizedQuery);
                        }}
                    >
                        <span className="editor-comments-email-avatar">@</span>
                        <span className="editor-comments-mention-option-copy">
                            <span className="editor-comments-mention-option-name">{normalizedQuery}</span>
                            <small>{t("COMMENT_INVITE_REVIEWER", "Invite reviewer")}</small>
                        </span>
                    </button>
                </>
            ) : null}
        </div>
    );
}

MentionMenu.propTypes = {
    query: PropTypes.string,
    members: PropTypes.array.isRequired,
    onSelect: PropTypes.func.isRequired,
};

function getMentionQuery(text, caretIndex) {
    const prefix = text.slice(0, caretIndex);
    const match = prefix.match(/(^|\s)@(\S*)$/);
    return match ? match[2] : null;
}

function CommentComposer({ members, onSubmit, onMentionQueryChange, placeholder, disabled, disableSubmit, isSubmitting, autoFocus, submitLabel, submitIconClassName, extraActionButton }) {
    const [text, setText] = useState("");
    const [mentionQuery, setMentionQuery] = useState(null);
    const [mentionMembers, setMentionMembers] = useState(members || []);
    const textareaRef = useRef(null);
    const currentMember = members && members.length ? members[0] : null;
    const cleanedText = text.trim();
    const submitDisabled = disabled || disableSubmit || isSubmitting || !cleanedText;
    const isSubmitInProgress = isSubmitting && !!cleanedText;

    useEffect(function() {
        if (autoFocus && textareaRef.current) {
            textareaRef.current.focus();
        }
    }, [autoFocus]);

    useEffect(function() {
        setMentionMembers(members || []);
    }, [members]);

    useEffect(function() {
        if (mentionQuery === null || !onMentionQueryChange) {
            return undefined;
        }

        let isActive = true;
        const timeoutId = setTimeout(function() {
            Promise.resolve(onMentionQueryChange(mentionQuery)).then(function(nextMembers) {
                if (isActive && Array.isArray(nextMembers)) {
                    setMentionMembers(nextMembers);
                }
            }).catch(function() {
                if (isActive) {
                    setMentionMembers(members || []);
                }
            });
        }, 120);

        return function() {
            isActive = false;
            clearTimeout(timeoutId);
        };
    }, [mentionQuery, onMentionQueryChange, members]);

    const updateMentionQuery = function(nextText, caretIndex) {
        setMentionQuery(getMentionQuery(nextText, caretIndex));
    };

    const submit = function() {
        if (!cleanedText || submitDisabled) {
            return;
        }
        const result = onSubmit(cleanedText);
        Promise.resolve(result).then(function() {
            setText("");
            setMentionQuery(null);
        }).catch(function() {
            // The model owns the user-facing error state; keep the text so it can be retried.
        });
    };

    const insertMention = function(mentionText) {
        const textarea = textareaRef.current;
        const caretIndex = textarea ? textarea.selectionStart : text.length;
        const prefix = text.slice(0, caretIndex).replace(/(^|\s)@\S*$/, function(match, leadingWhitespace) {
            return leadingWhitespace + mentionText + " ";
        });
        const nextText = prefix + text.slice(caretIndex);
        setText(nextText);
        setMentionQuery(null);
        requestAnimationFrame(function() {
            if (textareaRef.current) {
                textareaRef.current.focus();
                textareaRef.current.selectionStart = prefix.length;
                textareaRef.current.selectionEnd = prefix.length;
            }
        });
    };

    return (
        <div className={mentionQuery !== null ? "editor-comments-composer with-mention-menu" : "editor-comments-composer"} onClick={stopEditorShortcutPropagation}>
            <div className="editor-comments-composer-input-row">
                <Avatar useEditorTheme={true} size={22} src={currentMember && currentMember.profileImageUrl ? currentMember.profileImageUrl : undefined}>
                    {currentMember && currentMember.profileImageUrl ? null : getAuthorInitials(currentMember)}
                </Avatar>
                <div className="editor-comments-composer-input-stack">
                    <textarea
                        ref={textareaRef}
                        value={text}
                        disabled={disabled || isSubmitting}
                        className="editor-comments-textarea"
                        placeholder={placeholder}
                        rows={2}
                        onChange={function(event) {
                            setText(event.target.value);
                            updateMentionQuery(event.target.value, event.target.selectionStart);
                        }}
                        onKeyUp={function(event) {
                            stopEditorShortcutPropagation(event);
                            updateMentionQuery(event.currentTarget.value, event.currentTarget.selectionStart);
                        }}
                        onKeyDown={function(event) {
                            stopEditorShortcutPropagation(event);
                            if (event.key === "Enter" && !event.shiftKey) {
                                event.preventDefault();
                                submit();
                            }
                        }}
                        onKeyPress={stopEditorShortcutPropagation}
                    />
                    {mentionQuery !== null ? (
                        <MentionMenu query={mentionQuery} members={mentionMembers} onSelect={insertMention} />
                    ) : null}
                </div>
            </div>
            <div className="editor-comments-composer-actions">
                <button
                    type="button"
                    className={"editor-comments-submit button_with_label " + (submitDisabled ? "disabled " : "") + (isSubmitInProgress ? "submitting" : "")}
                    disabled={submitDisabled}
                    aria-busy={isSubmitInProgress ? "true" : "false"}
                    onClick={function(event) {
                        stopEditorShortcutPropagation(event);
                        submit();
                    }}
                >
                    <label>{submitLabel || t("COMMENT", "Comment")}</label>
                    {isSubmitInProgress ? (
                        <span className="editor-comments-button-spinner" aria-hidden="true"></span>
                    ) : submitIconClassName ? (
                        <span className={"icon editor-comments-action-icon " + submitIconClassName}></span>
                    ) : null}
                </button>
                {extraActionButton}
            </div>
        </div>
    );
}

CommentComposer.propTypes = {
    members: PropTypes.array.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onMentionQueryChange: PropTypes.func,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    disableSubmit: PropTypes.bool,
    isSubmitting: PropTypes.bool,
    autoFocus: PropTypes.bool,
    submitLabel: PropTypes.string,
    submitIconClassName: PropTypes.string,
    extraActionButton: PropTypes.node,
};

function CommentLoadingSkeleton() {
    const themeConfig = useDefaultTrebbleAntDTokenConfig({ useEditorTheme: true });
    return (
        <div className="editor-comments-loading-skeleton" role="status" aria-label={t("LOADING", "Loading...")}>
            {[0, 1, 2].map(function(index) {
                return (
                    <Card
                        key={index}
                        useEditorTheme={true}
                        size="small"
                        hoverable={false}
                        className={`editor-comments-card editor-comments-skeleton-card search-result-item-card ${themeConfig.inDarkMode ? "" : "border-neutral-300"} bg-transparent`}
                    >
                        <TrebbleSkeleton
                            useEditorTheme={true}
                            active={true}
                            avatar={{ size: 22 }}
                            title={{ width: "52%" }}
                            paragraph={{ rows: 2, width: ["88%", "68%"] }}
                        />
                    </Card>
                );
            })}
        </div>
    );
}

function CommentThread({ comment, expanded, onExpand }) {
    const messages = getCommentMessages(comment);
    const threadClassName = messages.length > 1 ? "editor-comments-thread has-replies" : "editor-comments-thread";
    if (expanded || messages.length <= 3) {
        return (
            <div className={threadClassName}>
                {messages.map(function(message) {
                    return <CommentMessage key={message.id} message={message} />;
                })}
            </div>
        );
    }
    const hiddenCount = messages.length - 3;
    const visibleMessages = [messages[0]].concat(messages.slice(messages.length - 2));
    return (
        <div className={threadClassName}>
            <CommentMessage message={visibleMessages[0]} />
            <button type="button" className="editor-comments-hidden-replies" onClick={onExpand}>
                {hiddenCount + " " + (hiddenCount === 1 ? t("COMMENT_HIDDEN_REPLY", "hidden reply") : t("COMMENT_HIDDEN_REPLIES", "hidden replies"))}
            </button>
            {visibleMessages.slice(1).map(function(message) {
                return <CommentMessage key={message.id} message={message} />;
            })}
        </div>
    );
}

CommentThread.propTypes = {
    comment: PropTypes.object.isRequired,
    expanded: PropTypes.bool,
    onExpand: PropTypes.func.isRequired,
};

function CommentCard({ comment, selected, members, onClick, onReply, onResolve, onReopen, onMentionQueryChange, isSubmitting }) {
    const themeConfig = useDefaultTrebbleAntDTokenConfig({ useEditorTheme: true });
    const [expanded, setExpanded] = useState(false);
    const latestMessage = getCommentLatestMessage(comment);
    const latestAuthor = latestMessage && latestMessage.author ? latestMessage.author : {};
    const replyCount = Math.max(getCommentMessages(comment).length - 1, 0);
    const statusActionButton = comment.status === "resolved" ? (
        <button type="button" className="editor-comments-action button_with_label secondary border-2 border-black bg-gray" onClick={function(event) { event.stopPropagation(); onReopen(); }}>
            <label>{t("COMMENT_REOPEN", "Reopen")}</label>
            <span className="icon editor-comments-action-icon pe-7s-refresh-2"></span>
        </button>
    ) : (
        <button type="button" className="editor-comments-action button_with_label border-2 border-black" onClick={function(event) { event.stopPropagation(); onResolve(); }}>
            <label>{t("COMMENT_RESOLVE", "Resolve")}</label>
            <span className="icon editor-comments-action-icon pe-7s-check"></span>
        </button>
    );

    return (
        <Card
            useEditorTheme={true}
            size="small"
            hoverable={false}
            style={{ outlineStyle: selected ? "solid" : "none" }}
            className={`editor-comments-card search-result-item-card ${themeConfig.inDarkMode ? "" : "border-neutral-300"} bg-transparent ${selected ? `selected search-result-item-card-selected outline-2 ${themeConfig.inDarkMode ? "" : "bg-white"} outline-offset-2 outline-solid ${themeConfig.inDarkMode ? "outline-primary" : "outline-black"}` : ""} ${comment.status === "resolved" ? "resolved" : ""}`}
            onClick={onClick}
        >
            <div
                className="flex flex-col editor-comments-card-content"
                role="button"
                tabIndex={0}
                onKeyDown={function(event) {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onClick();
                    }
                }}
            >
                {!selected && latestMessage ? (
                    <div className="editor-comments-card-preview">
                        <div className="editor-comments-message editor-comments-message-preview">
                            <Avatar useEditorTheme={true} size={22} src={latestAuthor.profileImageUrl || undefined}>
                                {!latestAuthor.profileImageUrl ? getAuthorInitials(latestAuthor) : null}
                            </Avatar>
                            <div className="editor-comments-message-body">
                                <div className="editor-comments-message-meta">
                                    <span className="editor-comments-author">{latestAuthor.name || latestAuthor.email || t("UNKNOWN", "Unknown")}</span>
                                    <span>{formatCompactRelativeTime(latestMessage.createdAt)}</span>
                                </div>
                                <div className="editor-comments-snippet">
                                    <CommentText text={latestMessage.body} />
                                </div>
                            </div>
                        </div>
                    </div>
                ) : null}
                {replyCount > 0 && !selected ? (
                    <div className="editor-comments-reply-count editor-comments-reply-count-preview">
                        {replyCount + " " + (replyCount === 1 ? t("REPLY", "reply") : t("COMMENT_REPLIES", "replies"))}
                    </div>
                ) : null}
                {selected ? (
                    <div className="editor-comments-card-selected">
                        <CommentThread comment={comment} expanded={expanded} onExpand={function() { setExpanded(true); }} />
                        {comment.status !== "resolved" ? (
                            <CommentComposer
                                members={members}
                                disabled={isSubmitting}
                                isSubmitting={isSubmitting}
                                placeholder={t("COMMENT_REPLY_PLACEHOLDER", "Reply or add others with @")}
                                submitLabel={t("REPLY", "Reply")}
                                submitIconClassName="ion-reply"
                                onMentionQueryChange={onMentionQueryChange}
                                extraActionButton={statusActionButton}
                                onSubmit={function(body) {
                                    return onReply(body);
                                }}
                            />
                        ) : null}
                        {comment.status === "resolved" ? (
                            <div className="flex flex-row items-center justify-end mt-1 editor-comments-card-actions">
                                {statusActionButton}
                            </div>
                        ) : null}
                        {replyCount > 0 ? (
                            <div className="flex flex-row items-center mt-1 editor-comments-card-actions">
                                <div className="flex flex-row items-center">
                                    <span className="editor-comments-reply-count">
                                        {replyCount + " " + (replyCount === 1 ? t("REPLY", "reply") : t("COMMENT_REPLIES", "replies"))}
                                    </span>
                                </div>
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </div>
        </Card>
    );
}

CommentCard.propTypes = {
    comment: PropTypes.object.isRequired,
    selected: PropTypes.bool,
    members: PropTypes.array.isRequired,
    onClick: PropTypes.func.isRequired,
    onReply: PropTypes.func.isRequired,
    onResolve: PropTypes.func.isRequired,
    onReopen: PropTypes.func.isRequired,
    onMentionQueryChange: PropTypes.func,
    isSubmitting: PropTypes.bool,
};

const EditorCommentsPanel = function(props) {
    const themeConfig = useDefaultTrebbleAntDTokenConfig({ useEditorTheme: true });
    const [filterMenuOpen, setFilterMenuOpen] = useState(false);
    const commentsData = useBackboneModel(props.commentsWidgetModel, function(model) {
        return {
            comments: model.getFilteredComments(),
            allComments: model.getComments(),
            activeCommentId: model.get("activeCommentId"),
            pendingSelection: model.get("pendingSelection"),
            mentionCandidates: model.getMentionCandidates(),
            filter: model.get("filter"),
            isLoading: model.get("isLoading"),
            isSubmitting: model.get("isSubmitting"),
            isPreparingSelection: model.get("isPreparingSelection"),
            errorMessage: model.get("errorMessage"),
            canMoveToPreviousComment: model.canMoveToPreviousComment(),
            canMoveToNextComment: model.canMoveToNextComment(),
            activeCommentIndex: model.getActiveCommentIndex(),
        };
    });

    useEffect(function() {
        if (props.onPanelOpen) {
            props.onPanelOpen();
        }
    }, []);

    const comments = commentsData.comments || [];
    const members = commentsData.mentionCandidates || [];
    const activeCommentId = commentsData.activeCommentId;
    const activeDisplayIndex = commentsData.activeCommentIndex >= 0 ? commentsData.activeCommentIndex + 1 : 0;
    const shouldShowLoadingSkeleton = commentsData.isLoading && !comments.length && !commentsData.pendingSelection;
    const searchMentionCandidates = useMemo(function() {
        return props.commentsWidgetModel.searchMentionCandidates
            ? props.commentsWidgetModel.searchMentionCandidates.bind(props.commentsWidgetModel)
            : null;
    }, [props.commentsWidgetModel]);

    const filterOptions = useMemo(function() {
        return [
            {
                key: "status",
                label: t("COMMENT_FILTER_STATUS", "Status"),
                options: [
                    { key: COMMENT_FILTER.OPEN, label: t("COMMENT_FILTER_OPEN", "Open") },
                    { key: COMMENT_FILTER.ALL, label: t("COMMENT_FILTER_ALL", "All") },
                    { key: COMMENT_FILTER.RESOLVED, label: t("COMMENT_FILTER_RESOLVED", "Resolved") },
                    { key: COMMENT_FILTER.FOR_ME, label: t("COMMENT_FILTER_FOR_ME", "For me") },
                ],
            },
        ];
    }, []);
    const filterValue = useMemo(function() {
        return { status: [commentsData.filter || COMMENT_FILTER.OPEN] };
    }, [commentsData.filter]);

    const onFilterChange = function(values) {
        const selectedFilters = values && values.status ? values.status : [];
        props.commentsWidgetModel.setFilter(selectedFilters[selectedFilters.length - 1] || COMMENT_FILTER.OPEN);
        setFilterMenuOpen(false);
    };

    return (
        <ConfigProvider theme={themeConfig}>
            <div
                className="editor-search-browse-panel editor-comments-panel flex flex-col p-2 h-full w-full max-w-sm relative"
                style={{ width: "310px" }}
                onKeyDown={stopEditorShortcutPropagation}
                onKeyUp={stopEditorShortcutPropagation}
                onKeyPress={stopEditorShortcutPropagation}
            >
                <div className="flex flex-col box-border p-2 search_context_panel editor-comments-context-panel">
                    <div className="flex flex-row items-center justify-between">
                        <b className="flex flex-col">{t("COMMENTS", "Comments").toUpperCase()}</b>
                        <div className="flex items-center">
                            <Tooltip title={t("CLOSE", "Close")} placement="bottom" align="center">
                                <button type="button" aria-label={t("CLOSE", "Close")} onClick={props.onPanelCloseButtonClick}>
                                    <span className="icon pe-7s-close text-3xl"></span>
                                </button>
                            </Tooltip>
                        </div>
                    </div>
                    <b className="py-2 text-left">{t("RESULTS", "Results")}</b>
                    <div className="flex flex-row justify-center items-center editor-comments-results-tools">
                        <div className="flex flex-col">
                            <div>{activeDisplayIndex} {t("OF", "of")} {comments.length}</div>
                        </div>
                        <div className="flex flex-col">
                            <Tooltip title={t("PREVIOUS_RESULT", "Previous result")} placement="bottom" align="center">
                                <button
                                    type="button"
                                    aria-label={t("PREVIOUS_RESULT", "Previous result")}
                                    disabled={!commentsData.canMoveToPreviousComment}
                                    className={!commentsData.canMoveToPreviousComment ? "disabled" : ""}
                                    onClick={function() { props.commentsWidgetModel.moveToPreviousComment(); }}
                                >
                                    <span className="icon ion-ios7-arrow-thin-up text-3xl"></span>
                                </button>
                            </Tooltip>
                        </div>
                        <div className="flex flex-col">
                            <Tooltip title={t("NEXT_RESULT", "Next result")} placement="bottom" align="center">
                                <button
                                    type="button"
                                    aria-label={t("NEXT_RESULT", "Next result")}
                                    disabled={!commentsData.canMoveToNextComment}
                                    className={!commentsData.canMoveToNextComment ? "disabled" : ""}
                                    onClick={function() { props.commentsWidgetModel.moveToNextComment(); }}
                                >
                                    <span className="icon ion-ios7-arrow-thin-down text-3xl"></span>
                                </button>
                            </Tooltip>
                        </div>
                        <div className="flex flex-col">
                            <TrebbleFilterCascader
                                useEditorTheme={true}
                                options={filterOptions}
                                value={filterValue}
                                open={filterMenuOpen}
                                onOpenChange={setFilterMenuOpen}
                                onChange={onFilterChange}
                                showSelectAll={false}
                                showSelectionIndicator={false}
                            >
                                <Tooltip title={t("FILTER_RESULTS", "Filter results")} placement="bottom" align="center">
                                    <button
                                        type="button"
                                        aria-label={t("FILTER_RESULTS", "Filter results")}
                                        onClick={function(event) {
                                            event.preventDefault();
                                        }}
                                        className={commentsData.filter !== COMMENT_FILTER.OPEN || filterMenuOpen ? "editor-comments-filter-toggle active flex items-center justify-center" : "editor-comments-filter-toggle flex items-center justify-center"}
                                    >
                                        <span className="icon pe-7s-filter text-2xl"></span>
                                    </button>
                                </Tooltip>
                            </TrebbleFilterCascader>
                        </div>
                    </div>
                    {commentsData.errorMessage ? (
                        <div className="editor-comments-error" role="status">
                            {commentsData.errorMessage}
                        </div>
                    ) : null}
                </div>
                {commentsData.pendingSelection ? (
                    <Card
                        useEditorTheme={true}
                        size="small"
                        hoverable={false}
                        style={{ outlineStyle: "solid" }}
                        className={`editor-comments-new-comment selected search-result-item-card search-result-item-card-selected ${themeConfig.inDarkMode ? "" : "border-neutral-300"} bg-transparent outline-2 ${themeConfig.inDarkMode ? "" : "bg-white"} outline-offset-2 outline-solid ${themeConfig.inDarkMode ? "outline-primary" : "outline-black"}`}
                    >
                        <div className="flex flex-col editor-comments-card-content">
                            <CommentComposer
                                members={members}
                                disabled={commentsData.isSubmitting}
                                disableSubmit={commentsData.isPreparingSelection}
                                isSubmitting={commentsData.isSubmitting}
                                autoFocus={true}
                                placeholder={t("COMMENT_PLACEHOLDER", "Add a comment")}
                                submitLabel={t("COMMENT", "Comment")}
                                submitIconClassName="ion-ios7-chatbubble-outline"
                                onMentionQueryChange={searchMentionCandidates}
                                onSubmit={function(body) {
                                    return props.commentsWidgetModel.submitPendingComment(body);
                                }}
                            />
                        </div>
                    </Card>
                ) : null}
                <div className="editor-comments-list add_gradient_at_bottom">
                    {shouldShowLoadingSkeleton ? (
                        <CommentLoadingSkeleton />
                    ) : null}
                    {!commentsData.isLoading && !comments.length && !commentsData.pendingSelection ? (
                        <div className="editor-comments-empty">
                            <Empty useEditorTheme={true} description={t("COMMENT_NO_COMMENTS", "No comments")} />
                        </div>
                    ) : null}
                    {comments.map(function(comment) {
                        const selected = comment.id === activeCommentId;
                        return (
                            <CommentCard
                                key={comment.id}
                                comment={comment}
                                selected={selected}
                                members={members}
                                isSubmitting={commentsData.isSubmitting}
                                onClick={function() {
                                    props.commentsWidgetModel.setActiveCommentId(comment.id, true);
                                }}
                                onReply={function(body) {
                                    return props.commentsWidgetModel.replyToComment(comment.id, body);
                                }}
                                onResolve={function() {
                                    return props.commentsWidgetModel.resolveComment(comment.id);
                                }}
                                onReopen={function() {
                                    return props.commentsWidgetModel.reopenComment(comment.id);
                                }}
                                onMentionQueryChange={searchMentionCandidates}
                            />
                        );
                    })}
                </div>
            </div>
        </ConfigProvider>
    );
};

EditorCommentsPanel.propTypes = {
    commentsWidgetModel: PropTypes.object.isRequired,
    onPanelOpen: PropTypes.func,
    onPanelCloseButtonClick: PropTypes.func.isRequired,
};

export default EditorCommentsPanel;
